import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Bell, MessageCircle, Send, Zap, Activity, Phone } from "lucide-react";
import { toast, Toaster } from "sonner";

const TABS = [
    { k: "push", label: "Web push", icon: Bell },
    { k: "whatsapp", label: "WhatsApp", icon: MessageCircle },
    { k: "reminders", label: "Reminders", icon: Zap },
    { k: "log", label: "Activity log", icon: Activity },
];

export default function AdminNotifications() {
    const [tab, setTab] = useState("push");
    const [counts, setCounts] = useState({});

    useEffect(() => {
        api.get("/admin/notifications/audience-counts").then(r => setCounts(r.data)).catch(() => {});
    }, []);

    return (
        <div className="space-y-5" data-testid="admin-notifications-page">
            <Toaster position="top-center" />
            <div>
                <div className="label-eyebrow text-sky-700">Engagement</div>
                <h1 className="font-serif text-4xl text-stone-900 inline-flex items-center gap-3"><Bell className="w-7 h-7 text-sky-700" />Notifications</h1>
                <p className="text-sm text-stone-500 mt-1">Web push to browsers (OneSignal) + WhatsApp messages (Twilio sandbox). Used automatically when orders ship, refills are due, or consultations are about to start.</p>
            </div>

            <div className="flex flex-wrap gap-1 border-b border-stone-200">
                {TABS.map(t => (
                    <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-3 -mb-px flex items-center gap-2 text-sm border-b-2 ${tab === t.k ? "border-sky-700 text-sky-700 font-semibold bg-white" : "border-transparent text-stone-500 hover:text-stone-900"}`} data-testid={`notif-tab-${t.k}`}>
                        <t.icon className="w-4 h-4" />{t.label}
                    </button>
                ))}
            </div>

            {tab === "push" && <PushTab />}
            {tab === "whatsapp" && <WaTab counts={counts} />}
            {tab === "reminders" && <RemindersTab />}
            {tab === "log" && <LogTab />}
        </div>
    );
}

// ---------------- PUSH ----------------
function PushTab() {
    const [form, setForm] = useState({ title: "", body: "", url: "" });
    const [busy, setBusy] = useState(false);
    const [testTarget, setTestTarget] = useState("");

    const broadcast = async () => {
        if (!form.title.trim() || !form.body.trim()) return toast.error("Title and body required");
        if (!window.confirm(`Send push to ALL subscribed users now?`)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/notifications/broadcast-push", form);
            if (data.sent) toast.success(`Sent to ${data.recipients} devices · id ${data.id?.slice(0, 8)}`);
            else toast.error(`Push failed: ${data.reason || "unknown"}`);
        } catch (e) { toast.error(e.response?.data?.detail); }
        finally { setBusy(false); }
    };
    const sendTest = async () => {
        if (!testTarget.trim()) return toast.error("Enter your user id or email-as-id");
        const { data } = await api.post("/admin/notifications/test", { channel: "push", target: testTarget, body: form.body || "Test push from admin", title: form.title || "Hahnemann test" });
        if (data.sent) toast.success(`Test push sent (recipients=${data.recipients ?? 0})`);
        else toast.error(`Failed: ${data.reason || "unknown"}`);
    };

    return (
        <div className="space-y-4" data-testid="push-tab">
            <div className="bg-white border border-stone-200 p-5 space-y-3">
                <h2 className="font-serif text-xl text-stone-900">Broadcast web push</h2>
                <p className="text-xs text-stone-500">Goes to every browser/device that has opted-in via the "Enable notifications" prompt.</p>
                <L label="Title (≤120 chars)"><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} maxLength={120} className="rounded-none border-stone-300" data-testid="push-title" /></L>
                <L label="Body (≤400 chars)"><Textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} rows={3} maxLength={400} className="rounded-none border-stone-300" data-testid="push-body" /></L>
                <L label="Click-through URL (optional)"><Input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="/shop?utm_source=push" className="rounded-none border-stone-300 font-mono text-xs" data-testid="push-url" /></L>
                <Button onClick={broadcast} disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="push-broadcast"><Send className="w-4 h-4 mr-1" />{busy ? "Sending…" : "Broadcast to all subscribers"}</Button>
            </div>
            <div className="bg-stone-50 border border-stone-200 p-5 space-y-2">
                <div className="text-xs font-semibold text-stone-700 uppercase tracking-widest">Quick test</div>
                <p className="text-[11px] text-stone-500">Enter your user id (the same one set as OneSignal external_id) to send just to yourself.</p>
                <div className="flex gap-2">
                    <Input value={testTarget} onChange={e => setTestTarget(e.target.value)} placeholder="user-uuid…" className="rounded-none border-stone-300 font-mono text-xs" data-testid="push-test-target" />
                    <Button onClick={sendTest} variant="outline" className="rounded-none" data-testid="push-test-send">Test</Button>
                </div>
            </div>
        </div>
    );
}

// ---------------- WHATSAPP ----------------
function WaTab({ counts }) {
    const [form, setForm] = useState({ audience: "all_customers", body: "" });
    const [busy, setBusy] = useState(false);
    const [testPhone, setTestPhone] = useState("");

    const blast = async () => {
        if (!form.body.trim()) return toast.error("Body required");
        const c = (counts[form.audience] || {});
        if (!window.confirm(`Send WhatsApp to ${c.with_phone || 0} users (${c.total || 0} in audience but only ${c.with_phone || 0} have phones)?`)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/notifications/broadcast-whatsapp", form);
            toast.success(`Sent: ${data.sent} · Failed: ${data.failed} · Skipped: ${data.skipped}`);
            if (data.failure_samples?.length) console.warn("WhatsApp failures:", data.failure_samples);
        } catch (e) { toast.error(e.response?.data?.detail); }
        finally { setBusy(false); }
    };
    const sendTest = async () => {
        if (!testPhone.trim()) return toast.error("Enter a phone number");
        const { data } = await api.post("/admin/notifications/test", { channel: "whatsapp", target: testPhone, body: form.body || "Test from Hahnemann admin", title: "Hahnemann" });
        if (data.sent) toast.success(`Test WhatsApp sent · ${data.id}`);
        else toast.error(`Failed: ${data.reason}`);
    };

    return (
        <div className="space-y-4" data-testid="wa-tab">
            <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex gap-2">
                <Phone className="w-4 h-4 mt-0.5" />
                <div>
                    <strong>Sandbox mode</strong>: each recipient must first text <code className="bg-amber-100 px-1">join &lt;your-code&gt;</code> to your Twilio sandbox number from WhatsApp. For production, get your Twilio number approved for WhatsApp Business API.
                </div>
            </div>
            <div className="bg-white border border-stone-200 p-5 space-y-3">
                <h2 className="font-serif text-xl text-stone-900">Broadcast WhatsApp</h2>
                <L label="Audience">
                    <Select value={form.audience} onValueChange={v => setForm({ ...form, audience: v })}>
                        <SelectTrigger className="rounded-none border-stone-300" data-testid="wa-audience"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all_customers">All customers — {counts.all_customers?.total || 0} ({counts.all_customers?.with_phone || 0} with phone)</SelectItem>
                            <SelectItem value="opted_in">Opted-in only — {counts.opted_in?.total || 0} ({counts.opted_in?.with_phone || 0} with phone)</SelectItem>
                            <SelectItem value="dormant_30d">Dormant 30d — {counts.dormant_30d?.total || 0} ({counts.dormant_30d?.with_phone || 0} with phone)</SelectItem>
                            <SelectItem value="active_subscribers">Active subscribers — {counts.active_subscribers?.total || 0} ({counts.active_subscribers?.with_phone || 0} with phone)</SelectItem>
                        </SelectContent>
                    </Select>
                </L>
                <L label="Message body — supports {name} placeholder">
                    <Textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} rows={6} maxLength={1400} placeholder={`Hi {name}, our monsoon flu remedies are now 20% off until Friday. Tap to shop:\nhttps://hplmedicines.com/shop?coupon=MONSOON20`} className="rounded-none border-stone-300 font-mono text-xs" data-testid="wa-body" />
                </L>
                <Button onClick={blast} disabled={busy} className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-stone-50" data-testid="wa-broadcast">
                    <Send className="w-4 h-4 mr-1" />{busy ? "Sending…" : "Send WhatsApp blast"}
                </Button>
            </div>

            <div className="bg-stone-50 border border-stone-200 p-5 space-y-2">
                <div className="text-xs font-semibold text-stone-700 uppercase tracking-widest">Quick test</div>
                <p className="text-[11px] text-stone-500">Send just to one phone first (you must join the sandbox from that phone).</p>
                <div className="flex gap-2">
                    <Input value={testPhone} onChange={e => setTestPhone(e.target.value)} placeholder="+919876543210" className="rounded-none border-stone-300 font-mono text-xs" data-testid="wa-test-phone" />
                    <Button onClick={sendTest} variant="outline" className="rounded-none" data-testid="wa-test-send">Test</Button>
                </div>
            </div>
        </div>
    );
}

// ---------------- REMINDERS ----------------
function RemindersTab() {
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const run = async (dry = false) => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/notifications/run-reminders?dry_run=${dry}`);
            setResult(data);
            if (data.subscription_due === 0 && data.appointment_soon === 0) toast.info("No reminders to send right now");
            else toast.success(`Sent: ${data.subscription_due} sub reminders + ${data.appointment_soon} appointment alerts${dry ? " (dry-run)" : ""}`);
        } catch (e) { toast.error(e.response?.data?.detail); }
        finally { setBusy(false); }
    };
    return (
        <div className="space-y-4" data-testid="reminders-tab">
            <div className="bg-white border border-stone-200 p-5">
                <h2 className="font-serif text-xl text-stone-900">Scheduled reminder sweep</h2>
                <p className="text-sm text-stone-600 mt-2">
                    Sends reminders for: <strong>subscriptions due today</strong> + <strong>appointments starting within the next 60 minutes</strong>.
                    Already-reminded items (today) are skipped automatically.
                </p>
                <p className="text-xs text-stone-500 mt-2">Set up a cron / scheduler to hit this every 15 minutes for full automation. For now, run it manually.</p>
                <div className="flex gap-2 mt-4">
                    <Button onClick={() => run(false)} disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="reminders-run"><Zap className="w-4 h-4 mr-1" />{busy ? "Running…" : "Run reminders now"}</Button>
                    <Button onClick={() => run(true)} disabled={busy} variant="outline" className="rounded-none" data-testid="reminders-dry">Dry run</Button>
                </div>
            </div>
            {result && (
                <div className="bg-stone-50 border border-stone-200 p-5">
                    <div className="text-sm font-semibold mb-2">Last sweep · {result.dry_run ? "(dry-run)" : "(live)"}</div>
                    <pre className="text-xs whitespace-pre-wrap font-mono bg-white border border-stone-200 p-3">{JSON.stringify(result, null, 2)}</pre>
                </div>
            )}
        </div>
    );
}

// ---------------- LOG ----------------
function LogTab() {
    const [rows, setRows] = useState([]);
    useEffect(() => { api.get("/admin/notifications/log").then(r => setRows(r.data)).catch(() => {}); }, []);
    return (
        <div className="bg-white border border-stone-200 overflow-x-auto" data-testid="log-tab">
            <table className="w-full text-sm">
                <thead><tr className="bg-stone-50 border-b border-stone-200">
                    <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">When</th>
                    <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Kind / user</th>
                    <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Title · body</th>
                    <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Push</th>
                    <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">WhatsApp</th>
                </tr></thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr><td colSpan={5} className="p-10 text-center text-sm text-stone-500"><Activity className="w-7 h-7 mx-auto mb-2 text-stone-300" />No notifications dispatched yet.</td></tr>
                    ) : rows.map((r, i) => {
                        const push = r.results?.push || (r.kind === "broadcast_push" ? r.results : null);
                        const wa = r.results?.whatsapp || (r.kind === "broadcast_whatsapp" ? { sent: r.sent, failed: r.failed } : null);
                        return (
                            <tr key={i} className="border-b border-stone-100 hover:bg-sky-50/40">
                                <td className="p-3 text-[11px] text-stone-500 font-mono">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                                <td className="p-3 text-xs">
                                    <div className="font-medium text-stone-900">{r.kind || (r.user_id ? "user" : "—")}</div>
                                    {r.user_id && <div className="font-mono text-[10px] text-stone-500">{r.user_id.slice(0, 12)}…</div>}
                                </td>
                                <td className="p-3 text-xs max-w-xs">
                                    <div className="font-medium text-stone-900 truncate">{r.title || "—"}</div>
                                    <div className="text-stone-600 line-clamp-2 text-[11px]">{r.body || "—"}</div>
                                </td>
                                <td className="p-3 text-xs">{push?.sent ? <span className="text-emerald-700">✓ {push.recipients ?? "?"} devices</span> : <span className="text-stone-400">—</span>}</td>
                                <td className="p-3 text-xs">{wa?.sent ? <span className="text-emerald-700">✓{wa.sent ? ` ${wa.sent}` : ""}</span> : <span className="text-stone-400">—</span>}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function L({ label, children }) { return <label className="block"><span className="text-[10px] uppercase tracking-widest text-stone-500">{label}</span><div className="mt-1">{children}</div></label>; }
